import React from "react";
import ImageDropzone from "@/components/ImageDropzone";

/**
 * System-level architecture assessment input widget.
 *
 * A minimum of 2 categories is required. Each category needs:
 *   - name (e.g. "Hair care", "Skin care")
 *   - at least one brand pack image
 *   - competitor images for the same category shelf (recommended)
 *
 * `categories` is an array of:
 *   { id, name, packFiles: [{b64,mime,name,preview}], compFiles: [...] }
 *
 * The parent (NewAssessment) keeps the state; this component is dumb.
 */
export default function SystemCategoriesEditor({ categories, setCategories }) {
  const update = (id, patch) => {
    setCategories(categories.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };
  const add = () => {
    const id = `cat_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setCategories([...categories, { id, name: "", packFiles: [], compFiles: [] }]);
  };
  const remove = (id) => {
    if (categories.length <= 2) return; // enforce minimum 2
    setCategories(categories.filter((c) => c.id !== id));
  };

  return (
    <div data-testid="system-categories-editor">
      <div className="overline" style={{ marginBottom: 8 }}>
        Categories · minimum 2 required · {categories.length} added
      </div>
      <p className="mono" style={{ color: "var(--v-text-2)", fontSize: 11, lineHeight: 1.55, marginBottom: 16 }}>
        A system-level architecture audit needs the brand&apos;s presence in ≥ 2 categories.
        For each category, upload the brand&apos;s SKUs AND the competitive set from the same shelf.
        Verdict scores the fixed vs variable layer across the whole system.
      </p>

      {categories.map((c, i) => (
        <div
          key={c.id}
          data-testid={`system-category-${i}`}
          className="v-surface"
          style={{ padding: 20, marginBottom: 16, borderLeft: "3px solid var(--v-accent)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
            <div className="mono" style={{ color: "var(--v-accent)", fontSize: 11, letterSpacing: ".14em", fontWeight: 700 }}>
              {String(i + 1).padStart(2, "0")}
            </div>
            <input
              className="v-input"
              value={c.name}
              onChange={(e) => update(c.id, { name: e.target.value })}
              placeholder="Category name — e.g. Hair care, Skin care, Facial cleansing"
              data-testid={`system-category-name-${i}`}
              style={{ flex: 1, minWidth: 260 }}
            />
            {categories.length > 2 && (
              <button
                type="button"
                onClick={() => remove(c.id)}
                data-testid={`system-category-remove-${i}`}
                className="v-btn v-btn-ghost"
                style={{ color: "#dc2626", padding: "6px 12px", fontSize: 11 }}
              >
                Remove
              </button>
            )}
          </div>

          <ImageDropzone
            files={c.packFiles}
            setFiles={(files) => update(c.id, { packFiles: typeof files === "function" ? files(c.packFiles) : files })}
            label={`Brand SKUs in "${c.name || "this category"}" — REQUIRED`}
            testId={`system-cat-${i}-packs`}
          />
          <div style={{ marginTop: 16 }}>
            <ImageDropzone
              files={c.compFiles}
              setFiles={(files) => update(c.id, { compFiles: typeof files === "function" ? files(c.compFiles) : files })}
              label={`Competitor set for "${c.name || "this category"}" — recommended`}
              testId={`system-cat-${i}-comps`}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={add}
        data-testid="system-category-add-btn"
        className="v-btn v-btn-outline"
        style={{ marginTop: 4 }}
      >
        + Add another category
      </button>
    </div>
  );
}
